import React from 'react'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import type {DigitalVosExtensionBlock, DigitalVosFormViewModel, DigitalVosSignatureUrls} from './digitalVosFormTypes'

const LOGO = toAbsoluteUrl('/media/logos/Ad Astra Logo_Red.png')
const RED = '#C8102E'

const base: React.CSSProperties = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '11.5px',
  lineHeight: '1.45',
  color: '#000',
}

const PAGE: React.CSSProperties = {
  ...base,
  width: '850px',
  minHeight: '1100px',
  padding: '60px 75px 70px',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  fontFamily: "Calibri, 'Segoe UI', Candara, Arial, sans-serif",
  color: '#231f20',
}

const TD: React.CSSProperties = {
  border: '1px solid #000',
  padding: '5px 8px',
  verticalAlign: 'top',
}

const UNDERLINE: React.CSSProperties = {
  display: 'inline-block',
  borderBottom: '1px solid #000',
  minHeight: '14px',
  paddingLeft: '3px',
}

const PLAIN: React.CSSProperties = {
  display: 'inline-block',
  minHeight: '14px',
  paddingLeft: '3px',
}

function Logo() {
  return (
    <img
      src={LOGO}
      alt='Ad Astra'
      style={{height: '52px', width: 'auto', display: 'block'}}
    />
  )
}

function TopRowPlain({
  label1,
  val1,
  label2,
  val2,
}: {
  label1: string
  val1: string
  label2: string
  val2: string
}) {
  return (
    <div style={{display: 'flex', gap: '40px', marginBottom: '13px'}}>
      {[
        [label1, val1],
        [label2, val2],
      ].map(([lbl, val]) => (
        <div key={lbl} style={{display: 'flex', flex: 1, alignItems: 'flex-end'}}>
          <span style={{fontWeight: 'bold', whiteSpace: 'nowrap', marginRight: '5px'}}>{lbl}</span>
          <span style={{...PLAIN, flex: 1, whiteSpace: 'pre-wrap'}}>{val}</span>
        </div>
      ))}
    </div>
  )
}

function FieldRowPlain({
  label,
  value,
  marginBottom = '13px',
}: {
  label: string
  value: string
  marginBottom?: string
}) {
  return (
    <div style={{display: 'flex', alignItems: 'flex-end', marginBottom}}>
      <span style={{fontWeight: 'bold', whiteSpace: 'nowrap', marginRight: '5px'}}>{label}</span>
      <span style={{...PLAIN, flex: 1, whiteSpace: 'pre-wrap'}}>{value}</span>
    </div>
  )
}

function SignInOutRow({
  signInLabel,
  signInValue,
  signOutLabel,
  signOutValue,
  minHeight,
}: {
  signInLabel: string
  signInValue: string
  signOutLabel: string
  signOutValue: string
  minHeight?: string
}) {
  const cellStyle: React.CSSProperties = {
    border: '1px solid #231f20',
    padding: '14px 10px',
    fontSize: '15px',
    verticalAlign: 'top',
    ...(minHeight ? {minHeight} : {}),
  }
  return (
    <tr>
      <td style={{...cellStyle, width: '30%', textAlign: 'center', color: RED, fontWeight: 'bold'}}>
        {signInLabel}
      </td>
      <td style={{...cellStyle, width: '20%', whiteSpace: 'pre-wrap'}}>{signInValue}</td>
      <td style={{...cellStyle, width: '30%', textAlign: 'center', color: RED, fontWeight: 'bold'}}>
        {signOutLabel}
      </td>
      <td style={{...cellStyle, width: '20%', whiteSpace: 'pre-wrap'}}>{signOutValue}</td>
    </tr>
  )
}

function SignatureImage({
  src,
  alt,
  maxHeight = '44px',
}: {
  src: string
  alt: string
  maxHeight?: string
}) {
  const [failed, setFailed] = React.useState(false)
  if (failed) {
    return <span className='text-muted' style={{fontSize: '10px'}}>Signature unavailable</span>
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      style={{maxHeight, maxWidth: '100%', objectFit: 'contain', display: 'block'}}
    />
  )
}

function SignCellSignatureRow({
  signInImageUrl,
  signOutImageUrl,
}: {
  signInImageUrl?: string | null
  signOutImageUrl?: string | null
}) {
  const cellStyle: React.CSSProperties = {
    border: '1px solid #231f20',
    padding: '14px 10px',
    fontSize: '15px',
    verticalAlign: 'top',
    minHeight: '52px',
  }
  return (
    <tr>
      <td style={{...cellStyle, width: '30%', textAlign: 'center', color: RED, fontWeight: 'bold'}}>
        Signature:
      </td>
      <td style={{...cellStyle, width: '20%'}}>
        {signInImageUrl ? <SignatureImage src={signInImageUrl} alt='Sign in signature' /> : null}
      </td>
      <td style={{...cellStyle, width: '30%', textAlign: 'center', color: RED, fontWeight: 'bold'}}>
        Signature:
      </td>
      <td style={{...cellStyle, width: '20%'}}>
        {signOutImageUrl ? <SignatureImage src={signOutImageUrl} alt='Sign out signature' /> : null}
      </td>
    </tr>
  )
}

function ExtFieldPlain({label, value}: {label: string; value: string}) {
  return (
    <div style={{display: 'flex', alignItems: 'flex-end', marginBottom: '9px'}}>
      <span style={{fontSize: '11px', whiteSpace: 'nowrap', marginRight: '5px'}}>{label}</span>
      <span style={{...PLAIN, flex: 1, fontSize: '11px', whiteSpace: 'pre-wrap'}}>{value}</span>
    </div>
  )
}

function ExtensionBlock({n, ext}: {n: number; ext: DigitalVosExtensionBlock}) {
  const fields: [string, string][] = [
    ['Time:', ext.time],
    ['Name of staff member requesting extension:', ext.staffName],
    ['Contact of requesting staff member:', ext.staffPhone],
    ['Reason for extension request:', ext.reason],
    ['Estimated length of extension:', ext.length],
    ['Name of Ad Astra Project Manager:', ext.projectManager],
  ]
  return (
    <div style={{marginBottom: '22px'}}>
      <div style={{fontWeight: 'bold', marginBottom: '8px'}}>EXTENSION #{n}:</div>
      {fields.map(([lbl, val]) => (
        <ExtFieldPlain key={lbl} label={lbl} value={val} />
      ))}
    </div>
  )
}

function Page1({
  d,
  signatures,
}: {
  d: DigitalVosFormViewModel
  signatures: DigitalVosSignatureUrls
}) {
  return (
    <div className='form-page' style={PAGE}>
      <div style={{marginBottom: '26px', textAlign: 'center'}}>
        <Logo />
      </div>

      <TopRowPlain
        label1='Assignment Number:'
        val1={d.assignmentNumber}
        label2='Date and Time:'
        val2={d.dateAndTime}
      />
      <FieldRowPlain label='Interpreter:' value={d.interpreter} />
      <FieldRowPlain label='Client:' value={d.client} />
      <FieldRowPlain label='Language:' value={d.language} />
      <FieldRowPlain label='Estimated Duration:' value={d.estimatedDuration} />
      <FieldRowPlain label='Address:' value={d.address} />
      <FieldRowPlain label='Instructions:' value={d.instructions} marginBottom='0' />

      <div
        style={{
          textAlign: 'center',
          color: RED,
          fontWeight: 'bold',
          fontSize: '14.5px',
          margin: '26px 0 10px',
        }}
      >
        The information below is to be completed by the facility personnel only.
      </div>

      <div style={{display: 'flex', marginBottom: 0}}>
        {['Sign In Information', 'Sign Out Information'].map((h) => (
          <div
            key={h}
            style={{
              flex: 1,
              textAlign: 'center',
              color: RED,
              fontWeight: 'bold',
              fontSize: '14.5px',
              paddingBottom: '4px',
            }}
          >
            {h}
          </div>
        ))}
      </div>

      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '18px', tableLayout: 'fixed'}}>
        <tbody>
          <SignInOutRow
            signInLabel='Date:'
            signInValue={d.signInDate}
            signOutLabel='Date:'
            signOutValue={d.signOutDate}
          />
          <SignInOutRow
            signInLabel='Arrival Time:'
            signInValue={d.arrivalTime}
            signOutLabel='Departure Time:'
            signOutValue={d.departureTime}
          />
          <SignInOutRow
            signInLabel='Staff Name:'
            signInValue={d.signInStaffName}
            signOutLabel='Staff Name:'
            signOutValue={d.signOutStaffName}
          />
          <SignInOutRow
            signInLabel='Phone Number:'
            signInValue={d.signInPhone}
            signOutLabel='Phone Number:'
            signOutValue={d.signOutPhone}
          />
          <SignInOutRow
            signInLabel='Comments:'
            signInValue={d.signInComments}
            signOutLabel='Comments:'
            signOutValue={d.signOutComments}
            minHeight='52px'
          />
          <SignCellSignatureRow
            signInImageUrl={signatures.checkInStaff}
            signOutImageUrl={signatures.checkOut}
          />
        </tbody>
      </table>

      <div style={{display: 'flex', alignItems: 'flex-end', marginBottom: '26px', fontSize: '14.5px'}}>
        <span style={{color: RED, fontWeight: 'bold', whiteSpace: 'nowrap', marginRight: '6px'}}>
          Break Time (If Applicable):
        </span>
        <span style={{...UNDERLINE, flex: 1, minWidth: '120px'}}>{d.breakTimeMinutes}</span>
      </div>

      <div style={{textAlign: 'center', marginBottom: '14px'}}>
        <div style={{textDecoration: 'underline', fontWeight: 'bold', marginBottom: '3px'}}>
          For Interpreter
        </div>
        <div style={{fontWeight: 'bold', marginBottom: '10px'}}>
          SIGN BELOW, THEN UPLOAD THIS FORM TO THE CONNECT PLATFORM
        </div>
        <div
          style={{
            fontSize: '11px',
            lineHeight: '1.6',
            textAlign: 'justify',
            maxWidth: '640px',
            margin: '0 auto',
          }}
        >
          This Verification of Service form serves as an individual service agreement between the
          Interpreter and AD ASTRA and <strong>MUST</strong> be returned within 48 hours of the
          completion of the assignment. Failure to do so may result in non-payment at the
          discretion of Management. Invoices submitted without Verification of Service forms will
          NOT be processed for payment until AD ASTRA has received this form. By signing this form,
          the Interpreter hereby signifies that they have received, read, and understood the AD
          ASTRA Code of Professional Conduct (CPC) and are bound by the tenets within. If the
          Interpreter has not received a copy of the CPC, they are responsible for contacting AD
          ASTRA staff and requesting a copy.
        </div>
      </div>

      <div style={{display: 'flex', gap: '40px', margin: '18px 0 22px'}}>
        <div style={{display: 'flex', flex: 2, alignItems: 'flex-end'}}>
          <span style={{fontWeight: 'bold', marginRight: '6px', whiteSpace: 'nowrap'}}>
            Signature:
          </span>
          <span style={{...UNDERLINE, flex: 1, minHeight: '48px', display: 'flex', alignItems: 'center'}}>
            {signatures.interpreter ? (
              <SignatureImage src={signatures.interpreter} alt='Interpreter signature' maxHeight='46px' />
            ) : null}
          </span>
        </div>
        <div style={{display: 'flex', flex: 1, alignItems: 'flex-end'}}>
          <span style={{fontWeight: 'bold', marginRight: '6px', whiteSpace: 'nowrap'}}>Date:</span>
          <span style={{...UNDERLINE, flex: 1}}>{d.interpreterSignatureDate}</span>
        </div>
      </div>

      <div
        style={{textAlign: 'center', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px'}}
      >
        Travel Information (if applicable)
      </div>
      <table style={{width: '100%', borderCollapse: 'collapse'}}>
        <tbody>
          <tr>
            <td style={TD}>
              <strong>Start Mileage:</strong> <span>{d.startMileage}</span>
            </td>
            <td style={TD}>
              <strong>End Mileage:</strong> <span>{d.endMileage}</span>
            </td>
            <td style={TD}>
              <strong>Total Mileage:</strong> <span>{d.totalMileage}</span>
            </td>
          </tr>
          <tr>
            <td style={TD}>
              <strong>
                Start Travel
                <br />
                Time:
              </strong>{' '}
              <span>{d.startTravelTime}</span>
            </td>
            <td style={TD}>
              <strong>
                End Travel
                <br />
                Time:
              </strong>{' '}
              <span>{d.endTravelTime}</span>
            </td>
            <td style={TD}>
              <strong>
                Total Travel
                <br />
                Time:
              </strong>{' '}
              <span>{d.totalTravelTime}</span>
            </td>
          </tr>
          <tr>
            <td colSpan={2} style={TD}>
              <strong>Staff Name:</strong> <span>{d.travelStaffName}</span>
            </td>
            <td style={TD}>
              <strong>Staff Signature:</strong>{' '}
              <span style={{display: 'inline-block', verticalAlign: 'middle', minHeight: '44px'}}>
                {signatures.travelStaff ? (
                  <SignatureImage src={signatures.travelStaff} alt='Travel staff signature' />
                ) : null}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function Page2Extensions({
  d,
  extensions,
  pageIndex,
}: {
  d: DigitalVosFormViewModel
  extensions: DigitalVosExtensionBlock[]
  pageIndex: number
}) {
  const startNum = pageIndex * 2
  return (
    <div className='form-page' style={PAGE}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '26px',
        }}
      >
        <Logo />
        <div style={{textAlign: 'right', lineHeight: '1.6'}}>
          <div style={{fontSize: '11px'}}>Verification of Service Form</div>
          <div style={{fontSize: '11px', fontWeight: 'bold'}}>
            Primary Number (Office):(301) 408-4242
          </div>
        </div>
      </div>

      <div style={{textAlign: 'center', marginBottom: '18px'}}>
        <div style={{fontWeight: 'bold', fontSize: '13px', marginBottom: '2px'}}>
          {d.assignmentId ? `Assignment ID # ${d.assignmentId}` : 'Assignment ID #'}
        </div>
        <div style={{fontWeight: 'bold', fontSize: '13px'}}>Interpreter Extension Requests</div>
      </div>

      <div style={{fontSize: '11px', lineHeight: '1.6', marginBottom: '20px'}}>
        <strong>Instructions:</strong> For any assignment that requires an extension, please obtain
        the following information (onsite staff member name and number). Once you have the required
        information, please CALL Ad Astra (301-408-4242 during business hours or 202-302-3379
        afterhours, on weekends, or IF nobody answers the office number) Please make sure to always
        ask for the name of the Ad Astra project manager you spoke with, and write their name down
        as well. When you submit your paperwork, include this form, in order to guarantee payment
        for all time over two (2) hours.
      </div>

      {pageIndex === 0 && !d.hasExtensions ? (
        <p style={{fontStyle: 'italic', fontSize: '11px', marginBottom: '22px'}}>
          No Extensions were made.
        </p>
      ) : (
        extensions.map((ext, i) => (
          <ExtensionBlock key={`${startNum + i + 1}`} n={startNum + i + 1} ext={ext} />
        ))
      )}

      {pageIndex === 0 ? (
        <>
          <div
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '13px',
              marginBottom: '12px',
            }}
          >
            Interpreter Report Journal
          </div>
          <div style={{fontSize: '11px', lineHeight: '1.6', marginBottom: '12px'}}>
            <strong>Instructions:</strong> Please use the form below for any additional notes,
            including patient late or no-show. Call Ad Astra within 15 minutes if you are late (you
            must call Ad Astra 15 minutes PRIOR to start time of assignment), reassignment or
            additional assignment, Patient went in for surgery (call Ad Astra the minute the
            patient goes into surgery), etc.
          </div>
          <div style={{fontSize: '11px', marginBottom: '6px'}}>Interpreter Comments:</div>
          <div
            style={{
              border: '1px solid #000',
              minHeight: '180px',
              padding: '8px',
              fontSize: '11px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {d.interpreterComments}
          </div>
        </>
      ) : null}
    </div>
  )
}

function Page3({d}: {d: DigitalVosFormViewModel}) {
  return (
    <div className='form-page' style={PAGE}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '26px',
        }}
      >
        <Logo />
        <div style={{textAlign: 'right', lineHeight: '1.6'}}>
          <div style={{fontSize: '11px'}}>Assignment Log Form</div>
          <div style={{fontSize: '11px', fontWeight: 'bold'}}>
            Primary Number (Office):(301) 408-4242
          </div>
        </div>
      </div>

      <TopRowPlain
        label1='Assignment Number:'
        val1={d.assignmentNumber}
        label2='Date and Time:'
        val2={d.dateAndTime}
      />
      <FieldRowPlain label='Interpreter:' value={d.interpreter} />
      <FieldRowPlain label='Client:' value={d.client} />
      <FieldRowPlain label='Language:' value={d.language} />
      <FieldRowPlain label='Estimated Duration:' value={d.estimatedDuration} />
      <FieldRowPlain label='Address:' value={d.address} />
      <FieldRowPlain label='Instructions:' value={d.instructions} />
      <FieldRowPlain label='Consumer:' value={d.consumer} marginBottom='22px' />

      <div style={{textAlign: 'center', fontSize: '12px', margin: '0 0 28px'}}>
        This Form is for VIS and Third Party Platform Assignments only.
      </div>

      <div style={{marginBottom: '28px'}}>
        <div style={{fontWeight: 'bold', letterSpacing: '0.04em', marginBottom: '7px'}}>
          INTERPRETATION START TIME:{' '}
          <span style={{...UNDERLINE, minWidth: '110px'}}>{d.interpretationStartTime}</span>
        </div>
        <div style={{fontStyle: 'italic', fontSize: '11px', lineHeight: '1.55'}}>
          This is the time you are scheduled to start. Please be signed in and ready 5 minutes
          before this time. Only adjust time if interpreter is late.
        </div>
      </div>

      <div style={{marginBottom: '28px'}}>
        <div style={{fontWeight: 'bold', letterSpacing: '0.04em', marginBottom: '7px'}}>
          INTERPRETATION END TIME:{' '}
          <span style={{...UNDERLINE, minWidth: '110px'}}>{d.interpretationEndTime}</span>
        </div>
        <div style={{fontStyle: 'italic', fontSize: '11px', lineHeight: '1.55'}}>
          This is the actual time the call ends. Round time down to the nearest minute (5:15:37 pm
          =&gt; 5:15 pm)
        </div>
      </div>

      <div
        style={{fontWeight: 'bold', fontSize: '11.5px', lineHeight: '1.6', marginBottom: '12px'}}
      >
        Please note any issues that arose during the appointment, technical or otherwise (could not
        connect right away, call dropped, patient was late, etc.):
      </div>
      <div
        style={{
          border: '1px solid #000',
          minHeight: '200px',
          padding: '8px',
          fontSize: '11px',
          whiteSpace: 'pre-wrap',
        }}
      >
        {d.issueNotes}
      </div>
    </div>
  )
}

function chunkExtensions(
  extensions: DigitalVosExtensionBlock[],
  size: number
): DigitalVosExtensionBlock[][] {
  if (!extensions.length) return [[]]
  const chunks: DigitalVosExtensionBlock[][] = []
  for (let i = 0; i < extensions.length; i += size) {
    chunks.push(extensions.slice(i, i + size))
  }
  return chunks
}

export type DigitalVosFormProps = {
  data: DigitalVosFormViewModel
  signatures?: DigitalVosSignatureUrls
  showToolbar?: boolean
  onDownloadPdf?: () => void
  downloadingPdf?: boolean
}

export const DigitalVosForm: React.FC<DigitalVosFormProps> = ({
  data,
  signatures = {},
}) => {
  const extensionChunks = chunkExtensions(data.extensions, 2)

  return (
    <>
      <style>{`
        @media print {
          .digital-vos-toolbar { display: none !important; }
          .digital-vos-wrapper { padding: 0 !important; background: #fff !important; }
          @page { size: 8.5in 11in; margin: 0.54in 0.67in; }
          .form-page {
            width: 100% !important;
            min-height: auto !important;
            padding: 0 !important;
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: always;
          }
          .form-page:last-child { page-break-after: auto; }
        }
        @media screen {
          .digital-vos-wrapper { background: #d6d6d6; padding: 24px; }
          .form-page { box-shadow: 0 2px 14px rgba(0,0,0,0.28); margin: 0 auto 28px auto; }
        }
      `}</style>

      

      <div className='digital-vos-wrapper' id='digital-vos-form-root'>
        <Page1 d={data} signatures={signatures} />
        {extensionChunks.map((chunk, idx) => (
          <Page2Extensions key={`ext-page-${idx}`} d={data} extensions={chunk} pageIndex={idx} />
        ))}
        {data.showPage3 ? <Page3 d={data} /> : null}
      </div>
    </>
  )
}
